import { Suspense, lazy } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { Text } from 'foundations-components/components/Text';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import PanelOverlay from 'personalization-lib/components/shared/display/PanelOverlay';
import AccessBoundary from 'personalization-lib/components/shared/boundaries/AccessBoundary';
import withModalErrorHandling from 'personalization-lib/components/shared/hocs/withModalErrorHandling';
import useOnNavigation from 'personalization-lib/hooks/nav/useOnNavigation';
import { showOpenPanelErrorAlert } from 'personalization-lib/utils/alerts';
import { getPostCreateRedirectUrl } from 'personalization-lib/utils/urls';
import { redirectToPage } from 'personalization-lib/utils/location';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CreateVariantPanelContent = /*#__PURE__*/lazy(() => import('./panelContent/CreateVariantPanelContent'));
const CreateVariantPanel = ({
  showOverlay,
  onClose,
  onCreateSuccess = strategyId => redirectToPage(getPostCreateRedirectUrl(strategyId)),
  onCreateError,
  initialData
}) => {
  useOnNavigation(onClose);
  return /*#__PURE__*/_jsx(PanelOverlay, {
    hasOverlay: showOverlay,
    children: /*#__PURE__*/_jsxs(Panel, {
      width: 700,
      "data-test-id": "create-variant-panel",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose,
        "data-test-id": "create-variant-panel-close-button"
      }), /*#__PURE__*/_jsx(PanelHeader, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "personalizationLib.createVariantPanel.title"
          })
        })
      }), /*#__PURE__*/_jsx(AccessBoundary, {
        requiresWrite: true,
        children: /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
            grow: true
          }),
          children: /*#__PURE__*/_jsx(CreateVariantPanelContent, {
            onClose: onClose,
            onCreateSuccess: onCreateSuccess,
            onCreateError: onCreateError,
            initialData: initialData
          })
        })
      })]
    })
  });
};

/**
 * @internal
 * **DO NOT IMPORT THIS COMPONENT DIRECTLY IN YOUR APPLICATION.**
 *
 * This is an internal implementation component that is automatically managed by the
 * PersonalizationProvider and should NEVER be used directly by consumers.
 *
 * If you want to create a personalization variant, use:
 * - [CreateVariantButton](https://github.com/HubSpotEngineering/personalization-lib/blob/master/docs/VariantCreation.md#createvariantbutton) - Opens the variant creation panel
 *
 * These components handle all the necessary setup, validation, permissions, and context
 * management for you. Importing CreateVariantPanel directly will bypass critical setup
 * and likely result in broken functionality.
 *
 * @example
 * // ❌ WRONG - Do not do this
 * import CreateVariantPanel from 'personalization-lib/components/variantCreation/CreateVariantPanel';
 *
 * @example
 * // ✅ CORRECT - Use CreateVariantButton instead
 * import CreateVariantButton from 'personalization-lib/components/variantCreation/CreateVariantButton';
 *
 * For more information, see the [Variant Creation docs](https://github.com/HubSpotEngineering/personalization-lib/blob/master/docs/VariantCreation.md)
 */
export default withModalErrorHandling(CreateVariantPanel, {
  onComponentError: showOpenPanelErrorAlert
});